import { useMemo } from "react";
import { LockSceneInput } from "../3d";
import type { LockCalculatorFormValues } from "./form";
import { isBeamHeader, isDownwardVerticalLoad } from "./headerLock";

/** Real dimensions (mm) and catalog code of one Fastener item. */
export type FastenerSpec = {
  lengthMm: number | null;
  diameterMm: number | null;
  /** Codice a catalogo, etichetta della legenda dei colori nel viewer 3D. */
  code: string | null;
};

/** Real fastener dimensions, from the loaded calculation's Configuration —
 * the same catalog Fastener data BOM/results already use. Passed in
 * separately from the form since the 3D subsystem deliberately doesn't
 * read the app's persisted data shapes (see toLockSceneInput below). */
export type FastenerSpecs = {
  header: FastenerSpec;
  joist: FastenerSpec;
  inclined: FastenerSpec;
};

/** Backend defaults used when the form leaves a field empty: the connector's distance from the
 * joist top and, in offset mode, the joist top's offset below the column top. */
export type TopDistanceBase = { distanceDefault: number | null; offsetDefault: number | null };

/** Catalog code of each connector pair — legenda "Connector 1"/"Connector 2" in the 3D viewer. */
export type ConnectorCodes = readonly [string | null, string | null];

// The user's value, else the backend default; a beam header sits flush when there is none.
const effectiveTopDistance = (
  value: number | null,
  isColumn: boolean,
  base?: TopDistanceBase | null,
): number | null => value ?? base?.distanceDefault ?? (isColumn ? null : 0);

// Offset mode on a column header only: the user's value, else the backend default (the lock's c_min).
const effectiveTopOffset = (form: LockCalculatorFormValues, base?: TopDistanceBase | null): number | null => {
  if (form.headerMember.orientation !== "column" || form.joistMember.topAlignment !== "offset") return null;
  return form.joistMember.topOffsetFromHeader ?? base?.offsetDefault ?? null;
};

/** The form-side conditions of the wider header lock (the MIDI one is checked where the family is known). */
const headerLockApplies = (form: LockCalculatorFormValues): boolean =>
  isDownwardVerticalLoad(form.loads.vertical) && isBeamHeader(form.headerMember.orientation);

/**
 * Explicit boundary between the calculator domain form and the 3D subsystem.
 */
export const toLockSceneInput = (
  form: LockCalculatorFormValues,
  fastenerSpecs?: FastenerSpecs,
  connectorCodes?: ConnectorCodes,
  topDistanceBase?: TopDistanceBase | null,
  headerLockCode?: string | null,
): LockSceneInput => ({
  lateralResistanceConfig: form.lateralResistanceConfig,
  headerOrientation: form.headerMember.orientation === "column" ? "column" : "beam",
  // The 3D scene always needs a concrete pair count to render a scaffold —
  // defaults to 1 when the form has no selection yet, same as
  // headerOrientation above.
  connectorPairCount: form.connectorPairCount ?? 1,
  connectorPairLockItemIds: [form.connectorPairs[0].lockItemId, form.connectorPairs[1].lockItemId],
  connectorPairCodes: connectorCodes ?? [null, null],
  // The family guard lives in LockCalculator, which clears the choice when the lock stops being MIDI.
  headerLockItemId: headerLockApplies(form) ? form.bom.headerLockItemId : null,
  headerLockCode: headerLockApplies(form) && form.bom.headerLockItemId ? (headerLockCode ?? null) : null,
  header: { width: form.headerMember.width, height: form.headerMember.height },
  joist: {
    width: form.joistMember.width,
    height: form.joistMember.height,
    alphaDeg: form.joistMember.alphaDeg,
    betaDeg: form.joistMember.betaDeg,
    // Effective distance from the joist top: the user's value, else the backend default.
    connectorTopDistance: effectiveTopDistance(
      form.joistMember.connectorTopDistance,
      form.headerMember.orientation === "column",
      topDistanceBase,
    ),
    // Offset mode on a column header only; aligned (or a beam) has no joist-top offset.
    topOffsetFromHeader: effectiveTopOffset(form, topDistanceBase),
  },
  fasteners: {
    ...form.fasteners,
    headerLengthMm: fastenerSpecs?.header.lengthMm ?? null,
    joistLengthMm: fastenerSpecs?.joist.lengthMm ?? null,
    inclinedLengthMm: fastenerSpecs?.inclined.lengthMm ?? null,
    headerDiameterMm: fastenerSpecs?.header.diameterMm ?? null,
    joistDiameterMm: fastenerSpecs?.joist.diameterMm ?? null,
    inclinedDiameterMm: fastenerSpecs?.inclined.diameterMm ?? null,
    headerCode: fastenerSpecs?.header.code ?? null,
    joistCode: fastenerSpecs?.joist.code ?? null,
    inclinedCode: fastenerSpecs?.inclined.code ?? null,
  },
  lockstopItemId: form.lockstopItemId,
});

/**
 * `toLockSceneInput` con identita' stabile: l'oggetto cambia SOLO quando
 * cambiano i valori che la scena 3D legge. `useWatch({ control })` senza
 * `name` restituisce un nuovo `form` a ogni tasto di QUALSIASI campo, quindi
 * `useMemo(..., [form])` non basterebbe; `toLockSceneInput` e' pura, per cui
 * si confronta per valore via chiave serializzata. Senza, `useLockScene`
 * rigira `resolveScene` (e a cascata scene/lockstops/focus) a ogni render.
 */
export const useLockSceneInput = (
  form: LockCalculatorFormValues | undefined,
  fastenerSpecs?: FastenerSpecs,
  connectorCodes?: ConnectorCodes,
  topDistanceBase?: TopDistanceBase | null,
  headerLockCode?: string | null,
): LockSceneInput | null => {
  const next = form ? toLockSceneInput(form, fastenerSpecs, connectorCodes, topDistanceBase, headerLockCode) : null;
  const key = JSON.stringify(next);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `next` e' funzione di `key`
  return useMemo(() => next, [key]);
};
